import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";
import { NotifierService } from "src/app/_services/notifier.service";

type DisplayValue =
  | { type: "text"; text: string }
  | { type: "list"; items: string[] }
  | { type: "pairs"; pairs: PairValue[] }
  | { type: "object-list"; items: PairValue[][] };

type PairValue = {
  key: string;
  value: string | DisplayValue;
  isNested: boolean;
};

@Component({
  selector: "app-eks-request-review",
  templateUrl: "./eks-request-review.component.html",
  styleUrls: ["../eks-requests.styles.css"],
})
export class EksRequestReviewComponent implements OnInit {
  private role: string = (localStorage.getItem("role") || "").toLowerCase();
  urlPrefix: string = this.role == "admin" ? "admin" : "client";
  apiBaseUrl: string = environment.apiURL || APIService.API_ENDPOINTV3;
  requestId = ""; awsAccountId = ""; region = "";
  loading = false; error = ""; data: any = null;
  summaryRows: any[] = []; payloadRows: any[] = [];
  actionLoading = false;
  actionMessage = "";
  actionError = "";
  showApproveRejectButtons = false;
  showReasonModal = false;
  modalAction: "approve" | "reject" | null = null;
  reasonText = "";
  reasonError = "";

  constructor(private route: ActivatedRoute, private router: Router, private apiServer: APIService, private notifier: NotifierService) {}
  headers() { return { Authorization: localStorage.getItem("t") }; }

  ngOnInit(): void {
    this.requestId = (this.route.snapshot.paramMap.get("request_id") || "").trim();
    this.awsAccountId = (this.route.snapshot.queryParamMap.get("aws_account_id") || "").trim();
    this.region = (this.route.snapshot.queryParamMap.get("region") || "").trim();
    this.load();
  }

  private label(k: string) {
    const map: any = {
      request_id: "Request ID",
      requester_id: "Requested By",
      accountid_region: "Account + Region",
      aws_account_id: "AWS Account ID",
      cluster_arn: "Cluster ARN",
      cluster_api_status: "Cluster API Status",
      approval_stage: "Approval Stage",
      approval_reason: "Approval Reason",
      launch_justification: "Launch Justification",
      provisioning_status: "Provisioning Status",
      provisioning_error: "Provisioning Error",
      vpc_id: "VPC ID",
      subnet_ids: "Subnet IDs",
      security_group_ids: "Security Group IDs",
    };
    return map[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  private text(v: any) { return v === null || v === undefined || v === "" ? "-" : String(v); }
  private formatDateToIst(value: any): string {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }) + " IST";
  }
  private toDisplayValue(value: any): DisplayValue {
    if (value === null || value === undefined || value === "") return { type: "text", text: "-" };
    if (Array.isArray(value)) {
      const hasObjectItems = value.some((x) => x && typeof x === "object");
      if (hasObjectItems) return { type: "object-list", items: value.map((x) => this.objectToPairs(x)) };
      return { type: "list", items: value.map((x) => this.text(x)) };
    }
    if (typeof value === "object") return { type: "pairs", pairs: this.objectToPairs(value) };
    return { type: "text", text: this.text(value) };
  }
  private objectToPairs(value: any): PairValue[] {
    if (!value || typeof value !== "object") return [{ key: "Value", value: this.text(value), isNested: false }];
    return Object.keys(value).map((k) => {
      const val = value[k];
      const isNested = Array.isArray(val) || (val && typeof val === "object");
      return { key: this.label(k), value: isNested ? this.toDisplayValue(val) : this.text(val), isNested };
    });
  }
  getTagKey(tagItem: PairValue[]): string {
    return String(tagItem.find((x) => x.key.toLowerCase() === "key")?.value || "-");
  }
  getTagValue(tagItem: PairValue[]): string {
    return String(tagItem.find((x) => x.key.toLowerCase() === "value")?.value || "-");
  }

  async load() {
    if (!this.requestId || !this.awsAccountId || !this.region) { this.error = "Invalid request context."; return; }
    this.loading = true;
    this.notifier.loading(true);
    const url = `${this.apiBaseUrl}/${this.urlPrefix}/apis/eks_launch/api/eks/request/${encodeURIComponent(this.requestId)}?aws_account_id=${encodeURIComponent(this.awsAccountId)}&region=${encodeURIComponent(this.region)}`;
    const r = await this.apiServer.getDataPromis(url, {}, this.headers());
    this.loading = false;
    this.notifier.loading(false);
    if (!r || typeof r === "string") { this.error = "Unable to fetch request details."; return; }
    this.data = r;
    // this.showApproveRejectButtons = this.data?.disable_approve_reject_btn !== true;
    await this.checkApproveRejectEligibility();

    const summaryKeys = [
      "request_id", "requester_id", "aws_account_id", "region", "created_at",
      "approval_status", "approval_stage",
      "launch_justification",
      "provisioning_status"
    ];
    this.summaryRows = summaryKeys.map((k) => ({
      label: this.label(k),
      display: this.toDisplayValue(k === "created_at" ? this.formatDateToIst(r[k]) : r[k]),
    }));

    const payload = r.request_payload || {};
    const excluded = ["aws_account_id", "region"];
    const preferredOrder = [
      "cluster_name",
      "vpc_id",
      "cluster_role_arn",
      "subnet_ids",
      "security_group_ids",
      "kubernetes_version",
      "endpoint_public_access",
      "tags",
      "endpoint_private_access",
    ];
    const keys = [
      ...preferredOrder.filter((k) => payload[k] !== undefined && !excluded.includes(k)),
      ...Object.keys(payload).filter((k) => !preferredOrder.includes(k) && !excluded.includes(k)),
    ];
    this.payloadRows = keys.map((k) => ({ label: this.label(k), display: this.toDisplayValue(payload[k]) }));
  }

  async checkApproveRejectEligibility() {
    try {
      const emailId = localStorage.getItem("eId") || "";
      const url = `${this.apiBaseUrl}/${this.urlPrefix}/apis/eks_launch/api/eks/approval/can-act?request_id=${encodeURIComponent(this.requestId)}&service_name=EKS&email_id=${encodeURIComponent(emailId)}`;
      const res = await this.apiServer.getDataPromis(url, {}, this.headers());
      if (res && typeof res !== "string" && res.can_approve_reject === true) {
        this.showApproveRejectButtons = true;
      } else {
        this.showApproveRejectButtons = false;
      }
    } catch {
      this.showApproveRejectButtons = false;
    }
  }

  onApprove() {
    this.modalAction = "approve";
    this.reasonText = "";
    this.reasonError = "";
    this.showReasonModal = true;
  }

  onReject() {
    this.modalAction = "reject";
    this.reasonText = "";
    this.reasonError = "";
    this.showReasonModal = true;
  }

  closeReasonModal() {
    this.showReasonModal = false;
    this.modalAction = null;
    this.reasonText = "";
    this.reasonError = "";
  }

  submitDecision() {
    if (!this.modalAction) return;

    if (!this.reasonText.trim()) {
      this.reasonError = "Reason is required.";
      return;
    }

    if (this.modalAction === "approve") {
      this.approveRequest(this.reasonText.trim());
    } else {
      this.rejectRequest(this.reasonText.trim());
    }
  }

  async approveRequest(reason: string) {
    if (this.actionLoading || !this.data) return;
    this.actionMessage = "";
    this.actionError = "";
    this.actionLoading = true;
    this.notifier.loading(true);
    try {
      const url = `${this.apiBaseUrl}/${this.urlPrefix}/apis/eks_launch/eks-approval`;
      const payload = {
        requestId: this.requestId,
        action: "APPROVE",
        reason: reason
      };
      const approveResult: any = await this.apiServer.postDataPromis(url, payload, this.headers());
      if (!approveResult || typeof approveResult === "string") {
        this.actionError = "Unable to approve request.";
        this.closeReasonModal();
        return;
      }
      this.showApproveRejectButtons = false;
      this.actionMessage = String(approveResult.message || approveResult.msg || "Request approved successfully.");
      this.closeReasonModal();
      this.router.navigate(["/dash/operations/eks-dashboard"]);
    } catch {
      this.actionError = "Unable to approve request.";
      this.closeReasonModal();
    } finally {
      this.actionLoading = false;
      this.notifier.loading(false);
    }
  }

  async rejectRequest(reason: string) {
    if (this.actionLoading || !this.requestId) return;
    this.actionMessage = "";
    this.actionError = "";
    this.actionLoading = true;
    this.notifier.loading(true);
    try {
      const url = `${this.apiBaseUrl}/${this.urlPrefix}/apis/eks_launch/eks-approval`;
      const payload = {
        requestId: this.requestId,
        action: "REJECT",
        reason: reason
      };
      const result: any = await this.apiServer.postDataPromis(url, payload, this.headers());
      if (!result || typeof result === "string") {
        this.actionError = "Unable to reject request.";
        this.closeReasonModal();
        return;
      }
      this.actionMessage = String(result.message || result.msg || "Request rejected successfully.");
      this.closeReasonModal();
      this.router.navigate(["/dash/operations/eks-dashboard"]);
    } catch {
      this.actionError = "Unable to reject request.";
      this.closeReasonModal();
    } finally {
      this.actionLoading = false;
      this.notifier.loading(false);
    }
  }

  back() { this.router.navigate(["/dash/operations/eks-dashboard"]); }
}
